import React, { useEffect, useRef, useState } from 'react';
import { MapPin } from 'lucide-react';

// Market opportunity details + a decorative longitude (for the fly-through rotation).
export const MARKET_INFO = {
  'India': { lng: 78, fee: 'Home Market', format: 'All formats', note: 'Rapidly expanding across cities.' },
  'Malaysia': { lng: 102, fee: 'USD 250K – 500K', format: 'Express / Dine-In', note: 'High demand for Indian QSR.' },
  'China': { lng: 104, fee: 'USD 500K – 1M', format: 'Standard / Premium', note: 'Large untapped metro markets.' },
  'Japan': { lng: 138, fee: 'USD 400K – 800K', format: 'Premium Lounge', note: 'Premium dining opportunity.' },
  'Saudi Arabia': { lng: 45, fee: 'USD 300K – 700K', format: 'Dine-In / Premium', note: 'Fast-growing F&B sector.' },
  'Dubai / UAE': { lng: 55, fee: 'USD 350K – 900K', format: 'All formats', note: 'Global food capital, high footfall.' },
  'Europe': { lng: 10, fee: 'USD 500K – 1M+', format: 'Premium Lounge', note: 'Master franchise territories.' },
  'USA / America': { lng: -95, fee: 'USD 600K – 1M+', format: 'Standard / Premium', note: 'Largest expansion potential.' },
};

// longitude (-180..180) -> background-position-x percent (decorative)
const lngToPercent = (lng) => -((lng + 180) / 360) * 100 * 2;

export default function Globe({ selected, flySpeed = 0.05 }) {
  const mapRef = useRef(null);
  const posRef = useRef(0);
  const targetRef = useRef(null);
  const [showTip, setShowTip] = useState(false);
  const info = MARKET_INFO[selected] || {};

  useEffect(() => {
    if (selected && MARKET_INFO[selected]) {
      targetRef.current = lngToPercent(MARKET_INFO[selected].lng);
      setShowTip(false);
      const t = setTimeout(() => setShowTip(true), 700);
      return () => clearTimeout(t);
    }
  }, [selected]);

  useEffect(() => {
    let raf;
    const loop = () => {
      const map = mapRef.current;
      if (map) {
        if (targetRef.current !== null) {
          const diff = targetRef.current - posRef.current;
          if (Math.abs(diff) > 0.15) {
            posRef.current += diff * flySpeed;
          } else {
            posRef.current += 0.015; // gentle idle drift once arrived
          }
        } else {
          posRef.current += 0.03;
        }
        map.style.backgroundPositionX = `${posRef.current}%`;
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [flySpeed]);

  return (
    <div className="globe-stage" data-testid="global-globe">
      <div className="globe-atmosphere" />
      <div className="globe-sphere">
        <div className="globe-map" ref={mapRef} style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/globe/world.png)` }} />
        <div className="globe-shade" />
        <div className="globe-grid" />
        {/* Active market pin (center-front) */}
        <span className="globe-hotspot is-active" style={{ top: '44%', left: '50%' }} data-testid="globe-active-pin" />
      </div>
      <div className="globe-ring" />

      {/* Fly-to tooltip */}
      {showTip && (
        <div className="globe-tooltip" data-testid="globe-tooltip">
          <div className="flex items-center gap-1.5 text-orange-400 text-xs uppercase tracking-widest font-semibold">
            <MapPin className="w-3.5 h-3.5" /> {selected}
          </div>
          <div className="mt-2 space-y-1 text-left">
            <div className="flex justify-between gap-4 text-[13px]"><span className="text-white/50">Franchise Fee</span><span className="text-white font-semibold">{info.fee}</span></div>
            <div className="flex justify-between gap-4 text-[13px]"><span className="text-white/50">Formats</span><span className="text-white font-semibold">{info.format}</span></div>
          </div>
          <div className="mt-2 text-[12px] text-white/60 leading-snug">{info.note}</div>
        </div>
      )}
    </div>
  );
}
